import { openDB } from 'idb';
import { getEnv } from 'utils';
import {
  cloneCustomContents,
  normalizeCustomContents,
  normalizeStoredCustomContentItem,
} from 'pages/bulletin/pdf/utils/bulletinPdfCustomContent';
import {
  clearIntroFields,
  normalizePdfTemplate,
} from 'pages/bulletin/pdf/utils/bulletinPdfIntroFields';

const DB_VERSION = 1;
const STORE_CLASSIC = 'bulletinPdfDraftsClassic';
const STORE_DEFAULT = 'bulletinPdfDraftsDefault';
const LEGACY_STORE = 'bulletinDrafts';
export const BULLETIN_PDF_DRAFT_DEBOUNCE_MS = 2000;
const DEBOUNCE_MS = BULLETIN_PDF_DRAFT_DEBOUNCE_MS;

/** شناسهٔ DOM یادداشت‌های چارت/آمار در پیش‌نمایش PDF بولتن */
export function isBulletinPdfNoteDomId(fieldId) {
  return String(fieldId).startsWith('noteId-');
}

let dbPromise;
const memoryDraftByScope = new Map();
const readDraftInflight = new Map();
const persistTimers = new Map();
const persistInflight = new Map();

function dbName() {
  return `${getEnv('REACT_APP_NAME')}_BulletinPdfFieldEdits`;
}

function resolveScope(bulletinId, template) {
  const bulletinIdStr = String(bulletinId);
  const templateKey = normalizePdfTemplate(template);
  const cacheKey = `${templateKey}:${bulletinIdStr}`;
  const storeName = templateKey === 'classic' ? STORE_CLASSIC : STORE_DEFAULT;
  return { bulletinIdStr, templateKey, cacheKey, storeName };
}

function emptyDraft(bulletinId, templateKey) {
  return {
    bulletinId: String(bulletinId),
    template: templateKey,
    fields: {},
    removedIds: [],
    introductionEnabled: false,
    customContents: {},
    updatedAt: Date.now(),
  };
}

function normalizeDraft(rec, bulletinId, templateKey) {
  const fields =
    rec?.fields && typeof rec.fields === 'object' && !Array.isArray(rec.fields)
      ? { ...rec.fields }
      : {};
  const removedIds = Array.isArray(rec?.removedIds)
    ? rec.removedIds.map(String).filter(Boolean)
    : [];
  return {
    bulletinId: String(bulletinId),
    template: templateKey,
    fields,
    removedIds,
    introductionEnabled: Boolean(rec?.introductionEnabled),
    customContents: normalizeCustomContents(rec?.customContents),
    updatedAt: typeof rec?.updatedAt === 'number' ? rec.updatedAt : Date.now(),
  };
}

function cloneDraft(draft) {
  return {
    ...draft,
    fields: { ...draft.fields },
    removedIds: [...draft.removedIds],
    customContents: cloneCustomContents(draft.customContents),
  };
}

function getDb() {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('indexedDB unavailable'));
  }
  if (!dbPromise) {
    dbPromise = openDB(dbName(), DB_VERSION, {
      upgrade(db, _oldVersion, _newVersion, transaction) {
        if (!db.objectStoreNames.contains(STORE_CLASSIC)) {
          db.createObjectStore(STORE_CLASSIC, { keyPath: 'bulletinId' });
        }
        if (!db.objectStoreNames.contains(STORE_DEFAULT)) {
          db.createObjectStore(STORE_DEFAULT, { keyPath: 'bulletinId' });
        }
        if (db.objectStoreNames.contains(LEGACY_STORE)) {
          transaction.objectStore(LEGACY_STORE).clear();
          db.deleteObjectStore(LEGACY_STORE);
        }
      },
    });
  }
  return dbPromise;
}

async function loadDraftFromIdb(scope) {
  const { bulletinIdStr, templateKey, storeName } = scope;
  if (typeof indexedDB === 'undefined') {
    return emptyDraft(bulletinIdStr, templateKey);
  }

  try {
    const db = await getDb();
    const rec = await db.get(storeName, bulletinIdStr);
    return rec
      ? normalizeDraft(rec, bulletinIdStr, templateKey)
      : emptyDraft(bulletinIdStr, templateKey);
  } catch {
    return emptyDraft(bulletinIdStr, templateKey);
  }
}

async function loadDraftForBulletin(bulletinId, template) {
  const scope = resolveScope(bulletinId, template);
  const cached = memoryDraftByScope.get(scope.cacheKey);
  if (cached) return cloneDraft(cached);

  let inflight = readDraftInflight.get(scope.cacheKey);
  if (!inflight) {
    inflight = loadDraftFromIdb(scope).finally(() => {
      readDraftInflight.delete(scope.cacheKey);
    });
    readDraftInflight.set(scope.cacheKey, inflight);
  }

  const draft = await inflight;
  memoryDraftByScope.set(scope.cacheKey, draft);
  return cloneDraft(draft);
}

function getMemoryDraft(scope) {
  if (!memoryDraftByScope.has(scope.cacheKey)) {
    memoryDraftByScope.set(
      scope.cacheKey,
      emptyDraft(scope.bulletinIdStr, scope.templateKey)
    );
  }
  return memoryDraftByScope.get(scope.cacheKey);
}

function clearPersistTimer(cacheKey) {
  const t = persistTimers.get(cacheKey);
  if (t) {
    clearTimeout(t);
    persistTimers.delete(cacheKey);
  }
}

async function flushPersistDraft(bulletinId, template) {
  const scope = resolveScope(bulletinId, template);
  const existing = persistInflight.get(scope.cacheKey);
  if (existing) return existing;

  const work = (async () => {
    const draft = getMemoryDraft(scope);
    draft.updatedAt = Date.now();

    if (typeof indexedDB === 'undefined') {
      return;
    }
    const db = await getDb();
    await db.put(scope.storeName, {
      bulletinId: scope.bulletinIdStr,
      template: scope.templateKey,
      fields: { ...draft.fields },
      removedIds: [...draft.removedIds],
      introductionEnabled: Boolean(draft.introductionEnabled),
      customContents: cloneCustomContents(draft.customContents),
      updatedAt: draft.updatedAt,
    });
  })().finally(() => {
    persistInflight.delete(scope.cacheKey);
  });

  persistInflight.set(scope.cacheKey, work);
  return work;
}

function schedulePersistDraft(bulletinId, template) {
  const { cacheKey } = resolveScope(bulletinId, template);
  clearPersistTimer(cacheKey);
  persistTimers.set(
    cacheKey,
    setTimeout(() => {
      persistTimers.delete(cacheKey);
      flushPersistDraft(bulletinId, template).catch(() => {});
    }, DEBOUNCE_MS)
  );
}

function persistDraftChange(bulletinId, template, mutate) {
  if (bulletinId == null || bulletinId === '') return Promise.resolve();
  const scope = resolveScope(bulletinId, template);
  mutate(getMemoryDraft(scope));
  schedulePersistDraft(bulletinId, template);
  return flushPersistDraft(bulletinId, template);
}

export async function readBulletinPdfDraftAsync(bulletinId, template) {
  if (bulletinId == null || bulletinId === '') return null;
  return loadDraftForBulletin(bulletinId, template);
}

export function peekBulletinPdfDraft(bulletinId, template) {
  if (bulletinId == null || bulletinId === '') return null;
  const { cacheKey } = resolveScope(bulletinId, template);
  if (!memoryDraftByScope.has(cacheKey)) return null;
  return cloneDraft(memoryDraftByScope.get(cacheKey));
}

export function peekBulletinPdfRemovedDomIds(bulletinId, template) {
  if (bulletinId == null || bulletinId === '') return [];
  const { cacheKey } = resolveScope(bulletinId, template);
  if (!memoryDraftByScope.has(cacheKey)) return null;
  return [...memoryDraftByScope.get(cacheKey).removedIds];
}

export async function readRemovedDomIdsAsync(bulletinId, template) {
  if (bulletinId == null || bulletinId === '') return [];
  const peeked = peekBulletinPdfRemovedDomIds(bulletinId, template);
  if (peeked != null) return peeked;
  const draft = await loadDraftForBulletin(bulletinId, template);
  return [...draft.removedIds];
}

export async function writeBulletinPdfFieldHtmlToLocal(
  bulletinId,
  template,
  fieldId,
  html
) {
  if (fieldId == null) return;
  return persistDraftChange(bulletinId, template, (draft) => {
    draft.fields = {
      ...draft.fields,
      [String(fieldId)]: html == null ? '' : String(html),
    };
  });
}

export async function removeBulletinPdfFieldAsync(
  bulletinId,
  template,
  fieldId
) {
  if (fieldId == null) return;
  const fid = String(fieldId);
  return persistDraftChange(bulletinId, template, (draft) => {
    const fields = { ...draft.fields };
    delete fields[fid];
    draft.fields = fields;
  });
}

export async function writeBulletinPdfIntroductionEnabled(
  bulletinId,
  template,
  enabled
) {
  return persistDraftChange(bulletinId, template, (draft) => {
    draft.introductionEnabled = Boolean(enabled);
    if (!enabled) {
      draft.fields = clearIntroFields(draft.fields);
    }
  });
}

export async function appendCustomContentAsync(
  bulletinId,
  template,
  platform,
  item
) {
  const platformKey = String(platform);
  const stored = normalizeStoredCustomContentItem(item);
  if (!stored) return;

  return persistDraftChange(bulletinId, template, (draft) => {
    const customContents = cloneCustomContents(draft.customContents);
    customContents[platformKey] = [
      ...(customContents[platformKey] || []),
      stored,
    ];
    draft.customContents = customContents;
  });
}

export async function addRemovedDomIdAsync(bulletinId, template, domId) {
  if (domId == null) return;
  const did = String(domId);
  return persistDraftChange(bulletinId, template, (draft) => {
    if (!draft.removedIds.includes(did)) {
      draft.removedIds = [...draft.removedIds, did];
    }
  });
}

export async function removeRemovedDomIdAsync(bulletinId, template, domId) {
  if (domId == null) return;
  const did = String(domId);
  return persistDraftChange(bulletinId, template, (draft) => {
    draft.removedIds = draft.removedIds.filter((id) => id !== did);
  });
}

export async function deleteBulletinPdfDraftAsync(bulletinId, template) {
  if (bulletinId == null || bulletinId === '') return;
  const scope = resolveScope(bulletinId, template);

  clearPersistTimer(scope.cacheKey);
  memoryDraftByScope.delete(scope.cacheKey);
  readDraftInflight.delete(scope.cacheKey);

  if (typeof indexedDB !== 'undefined') {
    try {
      const db = await getDb();
      await db.delete(scope.storeName, scope.bulletinIdStr);
    } catch {
      /* empty */
    }
  }
}

export function clearBulletinPdfDraftMemoryCache(bulletinId, template) {
  if (bulletinId == null || bulletinId === '') return;
  const { cacheKey } = resolveScope(bulletinId, template);
  memoryDraftByScope.delete(cacheKey);
  readDraftInflight.delete(cacheKey);
}

export async function flushBulletinPdfDraftNow(bulletinId, template) {
  if (bulletinId == null || bulletinId === '') return;
  const { cacheKey } = resolveScope(bulletinId, template);
  clearPersistTimer(cacheKey);
  await flushPersistDraft(bulletinId, template);
}
